import { useState, type CSSProperties } from 'react'
import type { RatioAiChange } from '../../../../data/ServerApiClient'

// Inline "AI changed this" badge for a ratio card. Click → a popover that shows what changed + why,
// the better metric the AI recommends instead (for demotes), and clear actions: use the recommendation,
// revert (reject the belief), or tell the AI how to handle it differently — via an INLINE field, never
// a browser prompt. Presentational + self-contained (inline styles, light/dark safe). The page supplies
// the mutators; `onRedirect` receives the instruction the badge composes (a canned "use X instead" or
// the user's free text).

const KIND: Record<string, { label: string; tone: string }> = {
  added: { label: 'AI added', tone: 'var(--good-ink, #1f7a4d)' },
  demoted: { label: 'AI de-prioritized', tone: 'var(--warn-ink, #92610a)' },
  reband: { label: 'AI re-tuned', tone: 'var(--accent, #3b6ef5)' },
}

export type RatioAiChangeBadgeProps = {
  change: RatioAiChange
  onRevert?: (claimId: string, originId: string) => void
  onRedirect?: (originId: string, instruction: string) => void
  busy?: boolean
}

export function RatioAiChangeBadge({ change, onRevert, onRedirect, busy }: RatioAiChangeBadgeProps) {
  const [open, setOpen] = useState(false)
  const [adjusting, setAdjusting] = useState(false)
  const [text, setText] = useState('')
  const kind = KIND[change.kind] ?? { label: 'AI changed', tone: 'var(--accent, #3b6ef5)' }
  const replacement = change.replacement_label?.trim() || null

  const close = () => {
    setOpen(false)
    setAdjusting(false)
    setText('')
  }
  const useInstead = () => {
    if (!replacement) return
    onRedirect?.(
      change.origin_id,
      `Replace this metric: drop it and make "${replacement}" the primary measure instead.`,
    )
    close()
  }
  const sendAdjust = () => {
    const t = text.trim()
    if (!t) return
    onRedirect?.(change.origin_id, t)
    close()
  }

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        title="FuseGrid's AI changed this metric — click to see why and adjust"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          padding: '3px 9px',
          borderRadius: 999,
          border: '1px solid var(--hair-strong, #d4d8de)',
          background: 'var(--panel, #fff)',
          color: kind.tone,
          cursor: 'pointer',
        }}
      >
        <span aria-hidden="true">✦</span>
        {kind.label}
      </button>
      {open && (
        <div
          role="dialog"
          aria-label="AI change details"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            zIndex: 20,
            width: 320,
            padding: 14,
            borderRadius: 12,
            border: '1px solid var(--hair, #e3e6ea)',
            background: 'var(--panel, #fff)',
            boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
            textAlign: 'left',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: kind.tone, marginBottom: 6 }}>
            {kind.label} this metric
          </div>
          <p style={{ margin: '0 0 12px', fontSize: 13, lineHeight: 1.5, color: 'var(--ink, #1b1f24)' }}>
            {change.assertion}
          </p>

          {replacement ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 11px',
                marginBottom: 12,
                borderRadius: 10,
                background: 'var(--canvas-2, #f6f8fa)',
                border: '1px solid var(--hair, #e3e6ea)',
              }}
            >
              <span style={{ flex: 1, fontSize: 12.5, color: 'var(--ink-2, #3a3f46)' }}>
                Focus on instead:{' '}
                <strong style={{ color: 'var(--ink, #1b1f24)' }}>{replacement}</strong>
              </span>
              <button
                type="button"
                disabled={busy}
                onClick={useInstead}
                style={actionStyle('var(--good-ink, #1f7a4d)', true)}
              >
                Use this instead
              </button>
            </div>
          ) : null}

          {!adjusting ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onRevert?.(change.claim_id, change.origin_id)}
                style={actionStyle('var(--neg-ink, #b42318)')}
              >
                Revert
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setAdjusting(true)}
                style={actionStyle('var(--ink-2, #3a3f46)')}
              >
                Tell the AI…
              </button>
              <button type="button" onClick={close} style={{ ...actionStyle('var(--muted, #6b7280)'), marginLeft: 'auto' }}>
                Keep
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                autoFocus
                rows={2}
                placeholder="e.g. lead with days-cash and DSO for liquidity, keep current ratio as a footnote"
                style={{
                  width: '100%',
                  fontSize: 13,
                  padding: '8px 10px',
                  borderRadius: 8,
                  border: '1px solid var(--hair-strong, #d4d8de)',
                  background: 'var(--panel, #fff)',
                  color: 'var(--ink, #1b1f24)',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  disabled={busy || !text.trim()}
                  onClick={sendAdjust}
                  style={actionStyle('var(--accent, #3b6ef5)', true)}
                >
                  Send to AI
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAdjusting(false)
                    setText('')
                  }}
                  style={{ ...actionStyle('var(--muted, #6b7280)'), marginLeft: 'auto' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  )
}

const actionStyle = (color: string, filled = false): CSSProperties => ({
  fontSize: 12,
  fontWeight: 600,
  padding: '5px 12px',
  borderRadius: 8,
  border: `1px solid ${filled ? color : 'var(--hair-strong, #d4d8de)'}`,
  background: filled ? 'var(--canvas-2, #f6f8fa)' : 'var(--panel, #fff)',
  color,
  cursor: 'pointer',
})

export default RatioAiChangeBadge
